import { useRef, useState } from 'react';

import {
  Box,
  Button,
  Center,
  Flex,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Table,
  Tabs,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  VStack,
  useClipboard,
  useDisclosure,
} from '@chakra-ui/react';
import { toast } from 'react-toastify';

import {
  useAffiliateProvidersQuery,
  useCreateAffiliateApiKeyMutation,
  useCreateAffiliateProviderMutation,
  useDeactivateAffiliateApiKeyMutation,
  useGetAffiliateApiKeysQuery,
} from '../../gql/generated/graphql';

import LeaseEndContainer from '../../components/Container/LEContainer';
import CardHeaderV2 from '../../components/shared/Card/components/CardHeaderV2';
import ApiKeySuccessModal from './components/ApiKeySuccessModal';
import CreateApiKeyModal from './components/CreateApiKeyModal';
import CreateProviderModal from './components/CreateProviderModal';
import DisableApiKeyDialog from './components/DisableApiKeyDialog';

import { formatDateAndTime } from '../../utils/dates';

const AffiliateApiKeys = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const { isOpen: isSuccessOpen, onOpen: onSuccessOpen, onClose: onSuccessClose } = useDisclosure();
  const {
    isOpen: isProviderFormOpen,
    onOpen: onProviderFormOpen,
    onClose: onProviderFormClose,
  } = useDisclosure();

  const [affiliateName, setAffiliateName] = useState('');
  const [selectedProviderId, setSelectedProviderId] = useState('');
  const [keyToDisable, setKeyToDisable] = useState<{ id: string; name: string } | null>(null);
  const [newApiKey, setNewApiKey] = useState('');
  const [hasUserCopied, setHasUserCopied] = useState(false);

  // Provider form state
  const [providerName, setProviderName] = useState('');
  const [providerSlug, setProviderSlug] = useState('');

  const { onCopy } = useClipboard(newApiKey);

  const cancelRef = useRef<HTMLButtonElement>(null);

  // Queries to fetch affiliate API keys and providers
  const {
    data: apiKeysData,
    loading: apiKeysLoading,
    error: apiKeysError,
    refetch: refetchApiKeys,
  } = useGetAffiliateApiKeysQuery();

  const {
    data: providersData,
    loading: providersLoading,
    error: providersError,
    refetch: refetchProviders,
  } = useAffiliateProvidersQuery();

  // Mutations for creating and disabling API keys
  const [createAffiliateApiKey, { loading: createLoading }] = useCreateAffiliateApiKeyMutation();
  const [deactivateAffiliateApiKey, { loading: disableLoading }] =
    useDeactivateAffiliateApiKeyMutation();
  const [createAffiliateProvider, { loading: createProviderLoading }] =
    useCreateAffiliateProviderMutation();

  const handleCreateApiKey = async () => {
    if (!affiliateName.trim()) {
      toast.error('Please enter an affiliate name');
      return;
    }

    try {
      const response = await createAffiliateApiKey({
        variables: {
          affiliateApiKey: {
            name: affiliateName,
            affiliate_provider_id: selectedProviderId || undefined,
          },
        },
      });

      // The API key is returned as a string in the response
      const apiKey = response.data?.createAffiliateApiKey;

      if (apiKey) {
        setNewApiKey(apiKey);
        setHasUserCopied(false); // Reset the copied state

        // Close the create modal and open the success modal
        onClose();
        onSuccessOpen();

        // Clear form fields
        setAffiliateName('');
        setSelectedProviderId('');

        // Refresh the list of API keys
        refetchApiKeys();
      } else {
        throw new Error('No API key returned from server');
      }
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create API key');
    }
  };

  const handleCreateProvider = async () => {
    if (!providerName.trim()) {
      toast.error('Please enter a provider name');
      return;
    }

    if (!providerSlug.trim()) {
      toast.error('Please enter a provider slug');
      return;
    }

    try {
      await createAffiliateProvider({
        variables: {
          affiliateProvider: {
            name: providerName,
            slug: providerSlug,
          },
        },
      });

      toast.success('Affiliate provider created successfully');

      // Close the form modal and reset fields
      onProviderFormClose();
      setProviderName('');
      setProviderSlug('');

      // Refresh the list of providers
      refetchProviders();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to create affiliate provider');
    }
  };

  const handleCopyApiKey = () => {
    onCopy();
    setHasUserCopied(true);

    toast.success('API Key Copied');
  };

  const handleSuccessModalClose = () => {
    // Show warning before closing if they haven't copied the key
    if (!hasUserCopied) {
      const confirmClose = window.confirm(
        'You have not copied your API key. It will not be shown again. Are you sure you want to close?',
      );
      if (!confirmClose) {
        return;
      }
    }

    onSuccessClose();
    setNewApiKey('');
    setHasUserCopied(false);
  };

  const openDisableConfirmation = (id: string, name: string) => {
    setKeyToDisable({ id, name });
    onConfirmOpen();
  };

  const handleDisableApiKey = async () => {
    if (!keyToDisable) {
      return;
    }

    try {
      await deactivateAffiliateApiKey({
        variables: {
          affiliateApiKeyId: keyToDisable.id,
        },
      });

      toast.success('API key disabled successfully');

      // Refresh the list of API keys
      refetchApiKeys();
      onConfirmClose();
      setKeyToDisable(null);
    } catch (err) {
      onConfirmClose();
      toast.error('Failed to disable API key');
    }
  };

  // Find provider name from ID
  const getProviderName = (providerId: string) => {
    if (!providerId || !providersData?.affiliateProviders) {
      return 'N/A';
    }

    const provider = providersData.affiliateProviders.find((p) => p?.id?.toString() === providerId);

    return provider?.name || 'N/A';
  };

  return (
    <>
      <Box py={7} px={{ base: 2, sm: 8 }} bgColor="queenBlue" />
      <LeaseEndContainer minH="calc(100vh - 100px)">
        <Box p={4} maxW="1200px" mx="auto">
          <CardHeaderV2
            title=""
            variant="rounded"
            flexDirection="column"
            alignItems="flex-start"
            padding="10px 30px"
          >
            <Flex
              justifyContent="space-between"
              alignItems="center"
              w="100%"
              fontWeight="bold"
              fontSize="18px"
            >
              <Text>Affiliate Management</Text>
              <Button colorScheme="blue" onClick={onOpen} isLoading={createLoading}>
                Create New API Key
              </Button>
            </Flex>
          </CardHeaderV2>

          <Tabs colorScheme="blue" mt={4}>
            <TabList>
              <Tab>API Keys</Tab>
              <Tab>Providers</Tab>
            </TabList>

            <TabPanels>
              {/* API Keys Panel */}
              <TabPanel p={0}>
                <VStack w="full" bgColor="white" p={6} borderRadius="md" boxShadow="sm">
                  {apiKeysLoading ? (
                    <Center w="full" py={10}>
                      <Spinner size="xl" color="blue.500" />
                    </Center>
                  ) : apiKeysError ? (
                    <Center w="full" py={10}>
                      <Text color="red.500">Error loading API keys: {apiKeysError.message}</Text>
                    </Center>
                  ) : (
                    <Box w="full">
                      <Table variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Affiliate Name</Th>
                            <Th>Provider</Th>
                            <Th>Status</Th>
                            <Th>Created At</Th>
                            <Th>Updated At</Th>
                            <Th>Actions</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {apiKeysData?.getAffiliateApiKeys?.length === 0 ? (
                            <Tr>
                              <Td colSpan={6} textAlign="center" py={4}>
                                No API keys found
                              </Td>
                            </Tr>
                          ) : (
                            apiKeysData?.getAffiliateApiKeys?.map((apiKey) => {
                              // Ensure id is not null or undefined
                              const keyId = apiKey?.id ? apiKey.id.toString() : '';
                              const keyName = apiKey?.name || 'Unknown';

                              return (
                                <Tr key={keyId || undefined}>
                                  <Td>{keyName}</Td>
                                  <Td>{getProviderName(apiKey?.affiliate_provider_id || '')}</Td>
                                  <Td>{apiKey?.is_active ? 'Active' : 'Inactive'}</Td>
                                  <Td>{formatDateAndTime(apiKey?.created_at)}</Td>
                                  <Td>{formatDateAndTime(apiKey?.updated_at)}</Td>
                                  <Td>
                                    {apiKey?.is_active && keyId && (
                                      <Button
                                        size="sm"
                                        colorScheme="red"
                                        onClick={() => openDisableConfirmation(keyId, keyName)}
                                        isLoading={disableLoading && keyToDisable?.id === keyId}
                                      >
                                        Disable
                                      </Button>
                                    )}
                                  </Td>
                                </Tr>
                              );
                            })
                          )}
                        </Tbody>
                      </Table>
                    </Box>
                  )}
                </VStack>
              </TabPanel>

              {/* Providers Panel */}
              <TabPanel p={0}>
                <VStack w="full" bgColor="white" p={6} borderRadius="md" boxShadow="sm">
                  <Flex justifyContent="flex-end" w="full" mb={4}>
                    <Button colorScheme="green" onClick={onProviderFormOpen}>
                      Create New Provider
                    </Button>
                  </Flex>

                  {providersLoading ? (
                    <Center w="full" py={10}>
                      <Spinner size="xl" color="blue.500" />
                    </Center>
                  ) : providersError ? (
                    <Center w="full" py={10}>
                      <Text color="red.500">Error loading providers: {providersError.message}</Text>
                    </Center>
                  ) : (
                    <Box w="full">
                      <Table variant="simple">
                        <Thead>
                          <Tr>
                            <Th>Name</Th>
                            <Th>Slug</Th>
                            <Th>Created At</Th>
                            <Th>Updated At</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {providersData?.affiliateProviders?.length === 0 ? (
                            <Tr>
                              <Td colSpan={4} textAlign="center" py={4}>
                                No providers found
                              </Td>
                            </Tr>
                          ) : (
                            providersData?.affiliateProviders?.map((provider) => {
                              const providerId = provider?.id ? provider.id.toString() : '';

                              return (
                                <Tr key={providerId || undefined}>
                                  <Td>{provider?.name || 'N/A'}</Td>
                                  <Td>{provider?.slug || 'N/A'}</Td>
                                  <Td>{formatDateAndTime(provider?.created_at)}</Td>
                                  <Td>{formatDateAndTime(provider?.updated_at)}</Td>
                                </Tr>
                              );
                            })
                          )}
                        </Tbody>
                      </Table>
                    </Box>
                  )}
                </VStack>
              </TabPanel>
            </TabPanels>
          </Tabs>
        </Box>
      </LeaseEndContainer>

      {/* Use the extracted modal components */}
      <CreateApiKeyModal
        isOpen={isOpen}
        onClose={onClose}
        affiliateName={affiliateName}
        setAffiliateName={setAffiliateName}
        selectedProviderId={selectedProviderId}
        setSelectedProviderId={setSelectedProviderId}
        handleCreateApiKey={handleCreateApiKey}
        createLoading={createLoading}
        providers={providersData?.affiliateProviders || []}
      />

      <CreateProviderModal
        isOpen={isProviderFormOpen}
        onClose={onProviderFormClose}
        providerName={providerName}
        setProviderName={setProviderName}
        providerSlug={providerSlug}
        setProviderSlug={setProviderSlug}
        handleCreateProvider={handleCreateProvider}
        createProviderLoading={createProviderLoading}
      />

      <ApiKeySuccessModal
        isOpen={isSuccessOpen}
        onClose={handleSuccessModalClose}
        newApiKey={newApiKey}
        hasUserCopied={hasUserCopied}
        handleCopyApiKey={handleCopyApiKey}
      />

      <DisableApiKeyDialog
        isOpen={isConfirmOpen}
        onClose={onConfirmClose}
        keyToDisable={keyToDisable}
        handleDisableApiKey={handleDisableApiKey}
        disableLoading={disableLoading}
        cancelRef={cancelRef}
      />
    </>
  );
};

export default AffiliateApiKeys;
